(() => {
  const cache = new Map();
  const norm = value => String(value || '').trim().toLowerCase();
  const state = { audience: 'all', expanded: false };
  const labels = { women: 'Women', men: 'Men' };

  const productUrl = href => {
    try {
      const url = new URL(href, location.origin);
      url.search = '';
      url.hash = '';
      url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
      return url.toString();
    } catch (_) {
      return '';
    }
  };

  const getProduct = href => {
    const url = productUrl(href);
    if (!url) return Promise.resolve(null);
    if (!cache.has(url)) {
      cache.set(url, fetch(url, { credentials: 'same-origin' })
        .then(response => response.ok ? response.json() : null)
        .catch(() => null));
    }
    return cache.get(url);
  };

  const audiences = (product, title) => {
    const raw = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',');
    const tags = raw.map(norm);
    const explicit = [];
    if (tags.includes('audience-women')) explicit.push('women');
    if (tags.includes('audience-men')) explicit.push('men');
    if (tags.includes('audience-unisex')) explicit.push('unisex');
    if (explicit.includes('unisex')) return ['women', 'men', 'unisex'];
    if (explicit.length) return [...new Set(explicit)];

    const type = norm(product?.type);
    const name = norm(product?.title || title);
    if (type.includes('women') || name.includes('for women') || name.includes("women's") || tags.includes('women')) return ['women'];
    if (type.includes('men') || name.includes('for men') || name.includes("men's") || tags.includes('men')) return ['men'];
    return ['women', 'men', 'unisex'];
  };

  const classify = async (card, selector) => {
    if (!card || card.dataset.kimAudienceReady === 'true') return;
    const link = card.querySelector(selector) || card.querySelector('a[href*="/products/"]');
    const product = link ? await getProduct(link.href) : null;
    const values = audiences(product, card.querySelector('h3')?.textContent || '');
    card.dataset.audiences = values.join('|');
    card.dataset.kimAudienceReady = 'true';

    if (values.includes('unisex') && !card.querySelector('[data-kim-unisex]')) {
      const badge = document.createElement('span');
      badge.className = 'kimhc-unisex-label';
      badge.dataset.kimUnisex = 'true';
      badge.textContent = 'Unisex';
      card.querySelector('.khp__image-link,.kim-solo-shop__card-media')?.append(badge);
    }
  };

  const matchesAudience = card => state.audience === 'all' ||
    norm(card.dataset.audiences).split('|').includes(state.audience);

  const setButtons = () => document.querySelectorAll('[data-kim-audience-option]').forEach(button => {
    const active = norm(button.dataset.kimAudienceOption) === state.audience;
    button.classList.toggle('is-selected', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
  });

  const controlHtml = label => `
    <div class="kimhc-audience-filter__inner">
      <span class="kimhc-audience-filter__label">${label}</span>
      <div class="kimhc-audience-filter__options" role="toolbar" aria-label="Filter products by audience">
        <button type="button" class="kimhc-audience-filter__option is-selected" data-kim-audience-option="all" aria-pressed="true">All</button>
        <button type="button" class="kimhc-audience-filter__option" data-kim-audience-option="women" aria-pressed="false">Women</button>
        <button type="button" class="kimhc-audience-filter__option" data-kim-audience-option="men" aria-pressed="false">Men</button>
      </div>
    </div>`;

  const insertControls = () => {
    const featured = document.querySelector('.shopify-section[id*="featured_products"]');
    if (featured && !document.querySelector('[data-kim-home-audience]')) {
      const section = document.createElement('section');
      section.className = 'kimhc-audience-filter kimhc-audience-filter--home';
      section.dataset.kimHomeAudience = 'true';
      section.setAttribute('aria-label', 'Shop products for women or men');
      section.innerHTML = `<div class="page-width">${controlHtml('Shop For')}</div>`;
      featured.before(section);
    }

    const solo = document.querySelector('[data-kim-solo-shop]');
    const controls = solo?.querySelector('.kim-solo-shop__controls');
    if (controls && !solo.querySelector('[data-kim-solo-audience]')) {
      const filter = document.createElement('div');
      filter.className = 'kimhc-audience-filter kimhc-audience-filter--solo';
      filter.dataset.kimSoloAudience = 'true';
      filter.innerHTML = controlHtml('Shop For');
      controls.before(filter);
    }
  };

  const homeVisible = card => !card.classList.contains('kimhc-audience-hidden') &&
    !card.classList.contains('khp-sfy-hidden');

  const updateHome = () => {
    ['featured_products', 'selected_for_you'].forEach(id => {
      const wrapper = document.querySelector(`.shopify-section[id*="${id}"]`);
      const cards = Array.from(wrapper?.querySelectorAll('.khp__card') || []);
      if (!cards.length) return;
      const count = cards.filter(homeVisible).length;
      let empty = wrapper.querySelector('[data-kim-audience-empty]');
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'kimhc-audience-empty';
        empty.dataset.kimAudienceEmpty = 'true';
        empty.textContent = 'No products match those filters.';
        wrapper.querySelector('.khp__grid')?.after(empty);
      }
      empty.hidden = count > 0;

      if (id === 'selected_for_you') {
        const color = wrapper.querySelector('.khp-sfy-filter__button.is-selected')?.dataset.color || '';
        const status = wrapper.querySelector('.khp-sfy-filter__status');
        const oldEmpty = wrapper.querySelector('.khp-sfy-empty');
        if (status) {
          const audience = state.audience === 'all' ? '' : ` for ${labels[state.audience]}`;
          status.textContent = `Showing ${count} selected product${count === 1 ? '' : 's'}${audience}${color ? ` in ${color}` : ''}`;
        }
        if (oldEmpty) oldEmpty.hidden = true;
      }
    });
  };

  const renderHome = () => {
    document.querySelectorAll(
      '.shopify-section[id*="featured_products"] .khp__card,.shopify-section[id*="selected_for_you"] .khp__card'
    ).forEach(card => card.classList.toggle('kimhc-audience-hidden', !matchesAudience(card)));
    updateHome();
  };

  const selected = (selector, key, fallback) =>
    norm(document.querySelector(`${selector}.is-selected`)?.dataset[key] || fallback);

  const variants = card => {
    if (card._kimAudienceVariants) return card._kimAudienceVariants;
    try {
      card._kimAudienceVariants = JSON.parse(card.querySelector('[data-variant-data]')?.textContent || '[]');
    } catch (_) {
      card._kimAudienceVariants = [];
    }
    return card._kimAudienceVariants;
  };

  const resetCard = card => {
    const image = card.querySelector('[data-card-image]');
    if (image?.dataset.originalSrc) {
      image.removeAttribute('srcset');
      image.src = image.dataset.originalSrc;
      image.alt = image.dataset.originalAlt || '';
    }
    card.querySelectorAll('[data-product-link]').forEach(link => {
      link.href = link.dataset.originalUrl || link.href;
    });
    const note = card.querySelector('[data-selected-color]');
    if (note) {
      note.hidden = true;
      note.textContent = '';
    }
  };

  const applyVariant = (card, color) => {
    if (!color) return resetCard(card);
    const found = variants(card).filter(item => norm(item.color) === color);
    const variant = found.find(item => item.available) || found[0];
    if (!variant) return resetCard(card);

    const image = card.querySelector('[data-card-image]');
    if (image && variant.image) {
      image.removeAttribute('srcset');
      image.src = variant.image;
      image.alt = variant.alt || `${card.querySelector('h3')?.textContent || 'Product'} in ${color}`;
    }
    card.querySelectorAll('[data-product-link]').forEach(link => {
      link.href = variant.url || link.dataset.originalUrl || link.href;
    });
    const note = card.querySelector('[data-selected-color]');
    if (note) {
      note.hidden = false;
      note.textContent = `Showing ${color}`;
    }
  };

  const renderSolo = () => {
    const root = document.querySelector('[data-kim-solo-shop]');
    if (!root) return;
    const cards = Array.from(root.querySelectorAll('[data-product-card]'));
    const color = selected('[data-color-filter]', 'colorFilter', '');
    const category = selected('[data-category-filter]', 'categoryFilter', 'all');
    const matches = cards.filter(card => {
      const colors = norm(card.dataset.colors).split('|').filter(Boolean);
      return matchesAudience(card) &&
        (category === 'all' || norm(card.dataset.category) === category) &&
        (!color || colors.includes(color));
    });

    let shown = 0;
    cards.forEach(card => {
      const show = matches.includes(card) && (state.expanded || shown < 8);
      card.hidden = !show;
      card.classList.toggle('is-initially-hidden', !show);
      if (show) {
        shown += 1;
        applyVariant(card, color);
      } else if (!matches.includes(card) || !color) {
        resetCard(card);
      }
    });

    const status = root.querySelector('[data-result-status]');
    if (status) {
      const audience = state.audience === 'all' ? '' : ` for ${labels[state.audience]}`;
      status.textContent = `Showing ${matches.length} product${matches.length === 1 ? '' : 's'}${audience}${color ? ` in ${color}` : ''}${category !== 'all' ? ` · ${category}` : ''}`;
    }
    const clear = root.querySelector('[data-clear-filters]');
    if (clear) clear.hidden = state.audience === 'all' && !color && category === 'all';
    const empty = root.querySelector('[data-empty-state]');
    if (empty) {
      empty.hidden = matches.length > 0;
      empty.textContent = 'No products are currently available for those audience, color and category filters.';
    }
    const view = root.querySelector('[data-view-all]');
    if (view) {
      view.hidden = matches.length <= 8;
      view.innerHTML = state.expanded
        ? 'Show Fewer Products <span aria-hidden="true">&uarr;</span>'
        : 'View All Products <span aria-hidden="true">&darr;</span>';
    }
  };

  const render = () => {
    setButtons();
    renderHome();
    renderSolo();
  };

  const bind = () => {
    document.addEventListener('click', event => {
      const view = event.target.closest('[data-view-all]');
      if (!view) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      state.expanded = !state.expanded;
      renderSolo();
    }, true);

    document.addEventListener('click', event => {
      const audience = event.target.closest('[data-kim-audience-option]');
      if (audience) {
        state.audience = norm(audience.dataset.kimAudienceOption) || 'all';
        state.expanded = false;
        render();
        return;
      }
      if (event.target.closest('[data-color-filter],[data-category-filter]')) {
        state.expanded = false;
        setTimeout(renderSolo, 0);
        return;
      }
      if (event.target.closest('[data-clear-filters]')) {
        state.audience = 'all';
        state.expanded = false;
        setButtons();
        setTimeout(renderSolo, 0);
        return;
      }
      if (event.target.closest('.khp-sfy-filter__button')) setTimeout(updateHome, 0);
    });
  };

  const observeSelected = () => {
    const wrapper = document.querySelector('.shopify-section[id*="selected_for_you"]');
    if (!wrapper || wrapper.dataset.kimAudienceObserved === 'true') return;
    wrapper.dataset.kimAudienceObserved = 'true';
    const observer = new MutationObserver(updateHome);
    wrapper.querySelectorAll('.khp__card').forEach(card =>
      observer.observe(card, { attributes: true, attributeFilter: ['class'] }));
  };

  const init = async scope => {
    insertControls();
    const home = Array.from(scope.querySelectorAll?.(
      '.shopify-section[id*="featured_products"] .khp__card,.shopify-section[id*="selected_for_you"] .khp__card'
    ) || []);
    const solo = Array.from(scope.querySelectorAll?.('[data-kim-solo-shop] [data-product-card]') || []);
    await Promise.allSettled([
      ...home.map(card => classify(card, '.khp__image-link')),
      ...solo.map(card => classify(card, '[data-product-link]'))
    ]);
    observeSelected();
    render();
  };

  const start = () => {
    bind();
    init(document);
  };
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', start, { once: true })
    : start();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();